import moment from "moment-timezone";
import { createSelector } from "reselect";
import {
  AlertMap,
  AlertPriority,
  AlertPriorityLevel,
  IAlertMonitor,
  Reminder,
  RemindersMap,
  StatusFilterMap,
} from ".";
import { DeviceStatusesNames } from "../devicesStatuses";
import { devicesStatusesSlicedByFilterNodeType } from "../devicesStatuses/selectors";
import { AppsStateOrganization } from "../organization";
import { organizationNodes } from "../organization/selectors";
import { ResidentConfigurationMap } from "../residentActivityPlan";
import {
  BedStatusValue,
  ResidentBedStatus,
  ResidentStatusMap,
} from "../residentStatuses";
import { bedStatusesSlicedByMonitorNode } from "../residentStatuses/selectors";
import { Resident, presenceStatuses } from "../residents";
import { residentsSlicedBySelectedMonitorNode } from "../residents/selectors";
import { IAppState } from "../types";
import { authReady } from "../userProfile/selectors";
import { fromAlertTokenToAppId } from "./index";

export const alertsAfterGate = createSelector(
  (state: IAppState) => state.alerts.alerts,
  (state: IAppState) => state.alerts.devicesAlerts,
  (state: IAppState) => state.devices.devices,
  (state: IAppState) => state.users.users,
  (state: IAppState) => state.devices.isLoading,
  (state: IAppState) => state.users.isLoading,
  (alerts, devicesAlerts, devices, users, isLoadingDevices, isLoadingUsers) => {
    if (
      Object.keys(devicesAlerts).length > 0 &&
      isLoadingDevices === false &&
      isLoadingUsers === false
    ) {
      const alertsCopy = { ...alerts };
      Object.keys(devicesAlerts).map((deviceId) => {
        const relatedResident = devices[deviceId]?.residentId;
        if (!relatedResident) return null;
        const fulfillNearbyAlerts = devicesAlerts[deviceId].map((el) => {
          const user = users[el?.user?.id];
          if (user)
            return {
              ...el,
              patientId: relatedResident,
              room: devices[deviceId].room,
              bed: devices[deviceId].bed,
              user: {
                firstName: user.firstName,
                lastName: user?.lastName,
                picture: user?.picture,
                id: user?.id,
              },
            };
          return { ...el, patientId: relatedResident };
        });
        if (alertsCopy[relatedResident])
          alertsCopy[relatedResident] =
            alertsCopy[relatedResident].concat(fulfillNearbyAlerts);
        else alertsCopy[relatedResident] = fulfillNearbyAlerts;
        return null;
      });
      return alertsCopy;
    } else if (
      Object.keys(devicesAlerts).length > 0 &&
      isLoadingDevices === false &&
      isLoadingUsers
    ) {
      const alertsCopy = { ...alerts };
      Object.keys(devicesAlerts).map((deviceId) => {
        const relatedResident = devices[deviceId]?.residentId;
        if (!relatedResident) return null;
        const fulfillNearbyAlerts = devicesAlerts[deviceId].map((el) => {
          return {
            ...el,
            patientId: relatedResident,
            room: devices[deviceId].room,
            bed: devices[deviceId].bed,
          };
        });
        if (alertsCopy[relatedResident])
          alertsCopy[relatedResident] =
            alertsCopy[relatedResident].concat(fulfillNearbyAlerts);
        else alertsCopy[relatedResident] = fulfillNearbyAlerts;
        return null;
      });
      return alertsCopy;
    }
    return alerts;
  }
);

export const remindersAfterGate = createSelector(
  (state: IAppState) => state.alerts.reminders,
  (reminders) => {
    return reminders;
  }
);

export const alertsAfterGateById = createSelector(
  alertsAfterGate,
  (_: IAppState, residentId: string): string => residentId,
  (alerts, residentId) => {
    return alerts[residentId] || [];
  }
);

export const remindersAfterGateById = createSelector(
  (state: IAppState) => state.alerts.reminders,
  (state: IAppState) => state.residents.residents,
  (_: IAppState, residentId: string): string => residentId,
  (reminders, residents, residentId) => {
    const resident = residents[residentId];
    if (resident.nodePresence !== presenceStatuses.active) return [];
    return reminders[residentId] || [];
  }
);

export const residentCardColor = () => {
  return createSelector(
    alertsAfterGateById,
    remindersAfterGateById,
    (state: IAppState) => state.residents.residents,
    (_: IAppState, residentId: string): string => residentId,
    (alerts, reminders, residents, residentId) => {
      const resident = residents[residentId];
      const filteredAlerts = alerts || [];
      const shouldBeRed =
        filteredAlerts.length > 0 &&
        filteredAlerts.some((el) => el.priority === AlertPriority.high);
      const shouldBeBlack =
        filteredAlerts.length > 0 &&
        filteredAlerts.some((el) => el.priority === AlertPriority.med);
      const shouldBeYellow =
        filteredAlerts.length > 0 && !shouldBeRed && !shouldBeBlack;
      if (shouldBeRed || shouldBeYellow || shouldBeBlack)
        return shouldBeRed
          ? AlertPriority.high
          : shouldBeBlack
          ? AlertPriority.med
          : shouldBeYellow
          ? AlertPriority.low
          : "";
      const filteredReminders =
        reminders && resident.nodePresence === presenceStatuses.active
          ? reminders
          : [];
      return filteredReminders.length > 0 ? AlertPriority.low : "";
    }
  );
};

const getResidentSortPriority = (
  presenceStatus: presenceStatuses,
  residentAlerts: IAlertMonitor[],
  residentReminders: Reminder[]
) => {
  let priority = 0;
  if (presenceStatus === presenceStatuses.away) return AlertPriorityLevel.AWAY;
  residentAlerts.map(
    (el) => (priority = el.priority > priority ? el.priority : priority)
  );
  if (residentReminders.length > 0)
    priority = priority > AlertPriority.low ? priority : AlertPriority.low;
  if (priority !== 0) {
    if (priority === AlertPriority.high) return AlertPriorityLevel.RED;
    if (priority === AlertPriority.med) return AlertPriorityLevel.BLACK;
    if (priority === AlertPriority.low) return AlertPriorityLevel.YELLOW;
  }

  return AlertPriorityLevel.NO_NOTIFICATION;
};

export const residentsStatusItemsToDisplay = createSelector(
  residentsSlicedBySelectedMonitorNode,
  devicesStatusesSlicedByFilterNodeType,
  bedStatusesSlicedByMonitorNode,
  alertsAfterGate,
  remindersAfterGate,
  (state: IAppState): ResidentConfigurationMap =>
    state.residentActivityPlan.configurations,
  organizationNodes,
  (
    residents,
    deviceStatuses,
    residentStatuses,
    alerts,
    reminders,
    configurations,
    organizationNodes
  ) => {
    const newResidents = Object.values(residents).concat();

    return sortResidentCards(
      newResidents.filter(
        filterCondition(alerts, reminders, residentStatuses, deviceStatuses)
      ),
      configurations,
      alerts,
      reminders,
      residentStatuses,
      deviceStatuses
    );
  }
);
const sortResidentCards = (
  newResidents: Resident[],
  configurations: ResidentConfigurationMap,
  alerts: AlertMap,
  reminders: RemindersMap,
  residentStatuses: ResidentStatusMap,
  deviceStatuses
): Resident[] => {
  const residentsToSort = newResidents;
  const residentsAway = residentsToSort.filter(
    (el) => el.nodePresence === presenceStatuses.away
  );
  const otherResidents = residentsToSort.filter(
    (res) => !residentsAway.includes(res)
  );
  const residentsWithNotifications = otherResidents.filter(
    (res) =>
      (alerts[res.id] !== undefined && alerts[res.id].length > 0) ||
      (reminders[res.id] !== undefined && reminders[res.id].length > 0)
  );
  const residentsWithoutNotifications = otherResidents.filter(
    (res) => !residentsWithNotifications.includes(res)
  );

  const sortUrgency = (a: Resident, b: Resident) => {
    try {
      const priorityA = getResidentSortPriority(
        a.nodePresence,
        alerts[a.id] || [],
        reminders[a.id] || []
      );
      const priorityB = getResidentSortPriority(
        b.nodePresence,
        alerts[b.id] || [],
        reminders[b.id] || []
      );
      if (priorityA !== priorityB) return priorityB - priorityA;
      const aLatestAlert: Reminder | IAlertMonitor = [
        ...(alerts[a.id] || []),
        ...(reminders[a.id] || []),
      ].sort(sortAlertsAndRemindersFromNewest)[0];
      const bLatestAlert = [
        ...(alerts[b.id] || []),
        ...(reminders[b.id] || []),
      ].sort(sortAlertsAndRemindersFromNewest)[0];
      const latestSortResult = sortAlertsAndRemindersFromNewest(
        aLatestAlert,
        bLatestAlert
      );
      if (latestSortResult === 0) {
        return sortNoNotif(a, b);
      }
      return latestSortResult;
    } catch (e) {
      console.log(e);
    }
  };
  const residentsWithoutConfig = residentsWithoutNotifications.filter(
    (res) =>
      configurations[res.id] === undefined ||
      Object.keys(configurations[res.id]).length === 0
  );
  const residentsWithConfig = residentsWithoutNotifications.filter(
    (res) => !residentsWithoutConfig.includes(res)
  );
  const sortNoNotif = (a: Resident, b: Resident) => {
    try {
      if (
        Object.keys(a?.devicesMinimized).length !==
        Object.keys(b?.devicesMinimized).length
      ) {
        return Object.keys(a?.devicesMinimized).length > 0 ? -1 : 1;
      }
      let isOfflineA = undefined;
      Object.keys(a?.devicesMinimized)?.map((el) => {
        if (isOfflineA === true) return null;
        if (!deviceStatuses[el + DeviceStatusesNames.ACTIVITY]?.isActive)
          isOfflineA = true;
        return null;
      });
      let isOfflineB = undefined;
      Object.keys(b?.devicesMinimized)?.map((el) => {
        if (isOfflineB === true) return null;
        if (!deviceStatuses[el + DeviceStatusesNames.ACTIVITY]?.isActive)
          isOfflineB = true;
        return null;
      });
      if (isOfflineA !== isOfflineB) {
        return isOfflineA ? 1 : isOfflineB ? -1 : 0;
      }
      const aHasConf =
        configurations[a.id] !== undefined &&
        Object.keys(configurations[a.id]).length !== 0;
      const bHasConf =
        configurations[b.id] !== undefined &&
        Object.keys(configurations[b.id]).length !== 0;
      if (aHasConf !== bHasConf) return aHasConf ? -1 : bHasConf ? 1 : 0;
      const statusA: ResidentBedStatus = residentStatuses[
        a.id
      ] as ResidentBedStatus;
      const statusB: ResidentBedStatus = residentStatuses[
        b.id
      ] as ResidentBedStatus;
      if (statusA?.value !== statusB?.value) {
        return statusA?.value === BedStatusValue.IN_BED
          ? -1
          : statusB?.value === BedStatusValue.IN_BED
          ? 1
          : 0;
      }

      return sortByName(a, b);
    } catch (e) {
      console.log(e);
    }
  };

  return residentsWithNotifications
    .sort(sortUrgency)
    .concat(residentsWithNotifications.length > 0 ? [undefined] : [])
    .concat(
      residentsWithConfig
        .sort(sortNoNotif)
        .concat(residentsWithoutConfig.sort(sortNoNotif))
    )
    .concat(residentsWithoutNotifications.length > 0 ? [undefined] : [])
    .concat(residentsAway.sort(sortByName));
};

export const allAlertsAndRemindersOnNode = createSelector(
  alertsAfterGate,
  remindersAfterGate,
  residentsSlicedBySelectedMonitorNode,
  (alerts, reminders, residents) => {
    let alertsTotal = [];
    let remindersTotal = [];
    residents.map((res: Resident) => {
      if (res.nodePresence === presenceStatuses.active) {
        if (reminders[res.id])
          remindersTotal = remindersTotal.concat(reminders[res.id]);
      }
      if (alerts[res.id]) alertsTotal = alertsTotal.concat(alerts[res.id]);
      return null;
    });
    return {
      alerts: alertsTotal,
      reminders: remindersTotal,
    };
  }
);

export const notificationsToDisplay = createSelector(
  allAlertsAndRemindersOnNode,
  residentsSlicedBySelectedMonitorNode,
  organizationNodes,
  (allAlertsAndReminders, residents, organizationNodes) => {
    const { alerts, reminders } = allAlertsAndReminders;
    const sortUrgencyAlert = (
      a: IAlertMonitor | Reminder,
      b: IAlertMonitor | Reminder
    ) => {
      const priorityA = a["activityType"] ? 1 : (a as IAlertMonitor).priority;
      const priorityB = b["activityType"] ? 1 : (b as IAlertMonitor).priority;
      if (priorityA === priorityB) {
        return sortByCreation(a, b) * -1;
      }
      return priorityB - priorityA;
    };

    const sortByCreation = (
      a: IAlertMonitor | Reminder,
      b: IAlertMonitor | Reminder
    ): number => {
      const aM = a["activityType"]
        ? moment(a.lastUpdate)
        : moment.utc(a.lastUpdate).local();
      const bM = b["activityType"]
        ? moment(b.lastUpdate)
        : moment.utc(b.lastUpdate).local();
      if (aM > bM) {
        return -1;
      }
      if (aM < bM) {
        return 1;
      }
      return 0;
    };
    const sortFun = sortUrgencyAlert;

    return alerts.concat(reminders).sort(sortFun);
  }
);

export const residentsSelectedByFilters = createSelector(
  residentsSlicedBySelectedMonitorNode,
  devicesStatusesSlicedByFilterNodeType,
  bedStatusesSlicedByMonitorNode,
  alertsAfterGate,
  remindersAfterGate,
  (_: IAppState, filters: StatusFilterMap): StatusFilterMap => filters,
  (
    residents,
    deviceStatuses,
    residentStatuses,
    alerts,
    reminders,
    alertsFilter
  ) => {
    console.log("residentsSelectedByFilters");

    return (residents as Resident[]).filter(
      filterCondition(alerts, reminders, residentStatuses, deviceStatuses)
    );
  }
);

const filterCondition = (
  alerts: AlertMap,
  reminders: RemindersMap,
  residentStatuses,
  deviceStatuses
) => {
  const condition = (item: Resident) => {
    return true;
  };
  return condition;
};

// const sortByRoom = (a: Resident, b: Resident) => {
//   const devicesA = Object.values(a.devicesMinimized).filter(
//     (el) => el.type === DeviceType.vitals
//   );
//   const devicesB = Object.values(b.devicesMinimized).filter(
//     (el) => el.type === DeviceType.vitals
//   );
//   if (devicesA.length === 0 && devicesB.length === 0) return sortByName(a, b);
//   if (devicesA.length === 0 && devicesB.length !== 0) return 1;
//   if (devicesA.length !== 0 && devicesB.length === 0) return -1;
//   let val = 0;
//   if (Number.parseInt(devicesA[0]?.room) < Number.parseInt(devicesB[0]?.room)) {
//     val = -1;
//   }
//   if (Number.parseInt(devicesA[0]?.room) > Number.parseInt(devicesB[0]?.room)) {
//     val = 1;
//   }
//   if (devicesA[0]?.room === devicesB[0]?.room) {
//     if (devicesA[0]?.bed?.toLowerCase() < devicesB[0]?.bed?.toLowerCase()) {
//       val = -1;
//     }
//     if (devicesA[0]?.bed?.toLowerCase() > devicesB[0]?.bed?.toLowerCase()) {
//       val = 1;
//     }
//   }
//   return val;
// };

const sortByName = (a: Resident, b: Resident) => {
  if (a.firstName.toLowerCase() < b.firstName.toLowerCase()) return -1;
  if (a.firstName.toLowerCase() > b.firstName.toLowerCase()) {
    return 1;
  }
  if (a.lastName.toLowerCase() < b.lastName.toLowerCase()) return -1;
  if (a.lastName.toLowerCase() > b.lastName.toLowerCase()) {
    return 1;
  }
  return 0;
};

export const monitorsReady = createSelector(
  authReady,
  (state: IAppState): boolean => state.residents.isLoading,
  (state: IAppState): boolean => state.devices.isLoading,

  (state: IAppState): AppsStateOrganization => state.organization,
  (mainLoaded, isLoading, isLoadingDevices, organization) => {
    return !mainLoaded
      ? false
      : isLoading !== false
      ? false
      : isLoadingDevices
      ? false
      : organization.isLoading === false;
  }
);

export const alertsByIds = createSelector(
  alertsAfterGate,
  (_: IAppState, residentsIs: string[]) => residentsIs,
  (alerts, residentsIs) => {
    let result = [];
    residentsIs.map((id: string) => {
      const resAl = alerts[id] || [];
      result = result.concat(resAl);
      return null;
    }, []);
    return result;
  }
);

export const hasAlerts = createSelector(
  alertsAfterGate,
  remindersAfterGate,
  residentsSlicedBySelectedMonitorNode,
  (alerts, reminders, residents) => {
    return residents.some((res: Resident) => {
      if (alerts[res.id] && alerts[res.id].length > 0) return true;
      if (reminders[res.id] && reminders[res.id].length > 0) return true;
      return null;
    });
  }
);

export const sortAlertsAndRemindersForCard = (
  a: IAlertMonitor | Reminder,
  b: IAlertMonitor | Reminder
): number => {
  const priorityA = a["priority"] ? a["priority"] : AlertPriority.low;

  const priorityB = b["priority"] ? b["priority"] : AlertPriority.low;
  if (priorityA !== priorityB) return priorityB - priorityA;

  return sortAlertsAndRemindersFromNewest(a, b);
};
export const sortAlertsAndRemindersFromNewest = (
  a: IAlertMonitor | Reminder,
  b: IAlertMonitor | Reminder
): number => {
  const aM = a["activityType"]
    ? moment(a.lastUpdate)
    : moment.utc(a.lastUpdate).local();
  const bM = b["activityType"]
    ? moment(b.lastUpdate)
    : moment.utc(b.lastUpdate).local();
  if (aM === bM) return 0;
  return aM > bM ? -1 : 1;
};
export const sortAlertsFromNewest = (
  a: IAlertMonitor,
  b: IAlertMonitor
): number => {
  if (a.priority !== b.priority) return b.priority - a.priority;
  const aM = moment.utc(a.lastUpdate).local();
  const bM = moment.utc(b.lastUpdate).local();
  if (aM === bM) return 0;
  return aM > bM ? -1 : 1;
};

export const sortRemindersFromNewest = (a: Reminder, b: Reminder): number => {
  const aM = moment(a.lastUpdate);
  const bM = moment(b.lastUpdate);
  if (aM === bM) return 0;
  return aM > bM ? -1 : 1;
};

export const replayedAlertsAndActivities = createSelector(
  residentsSlicedBySelectedMonitorNode,
  alertsAfterGate,
  remindersAfterGate,

  (state: IAppState) => state.repeatAlert.nodeDataNew,
  (residents, alerts, reminders, nodeData) => {
    let alertsFiltered: IAlertMonitor[] = [];
    let remindersFiltered = [];
    const byGroups = {};
    residents.map((el) => {
      const nodeRepeatSettings = nodeData[el.nodeId];
      if (el.nodePresence === presenceStatuses.active) {
        const residentReminders = reminders[el.id];
        if (residentReminders && residentReminders?.length > 0) {
          const residentActivities = residentReminders.filter(
            (el) =>
              nodeRepeatSettings?.notifications?.indexOf(el.activityType) !== -1
          );
          remindersFiltered = remindersFiltered.concat(residentActivities);
          if (!byGroups[el?.nodeId])
            byGroups[el?.nodeId] = {
              alerts: [],
              activities: residentActivities,
            };
          else
            byGroups[el?.nodeId] = {
              ...byGroups[el?.nodeId],
              activities:
                byGroups[el?.nodeId].activities.concat(residentActivities),
            };
        }
      }
      const residentAlerts = alerts[el.id];
      if (residentAlerts && residentAlerts?.length > 0) {
        const residebtAlFilt = residentAlerts.filter(
          (el) =>
            nodeRepeatSettings?.notifications?.indexOf(
              fromAlertTokenToAppId(el.alertToken)
            ) !== -1
        );
        alertsFiltered = alertsFiltered.concat(residebtAlFilt);
        if (!byGroups[el?.nodeId])
          byGroups[el?.nodeId] = { alerts: residebtAlFilt, activities: [] };
        else
          byGroups[el?.nodeId] = {
            ...byGroups[el?.nodeId],
            alerts: byGroups[el?.nodeId].alerts.concat(residebtAlFilt),
          };
      }
      return null;
    });

    return { alerts: alertsFiltered, activities: remindersFiltered, byGroups };
  }
);

export const activityNotificationsCreator = () => {
  return createSelector(
    (state: IAppState): RemindersMap => state.alerts.reminders,
    residentsSlicedBySelectedMonitorNode,
    (reminders, residents) => {
      const check = (residentId: string) => {
        if (reminders[residentId]) return true;

        return false;
      };

      return residents.some((el) => check(el.id));
    }
  );
};
